import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import useScheduledCalls from '../../hooks/useScheduledCalls';

export default function ScheduledCallsPage() {
  const { can, canInModule } = useAuth();
  const { scheduledCalls: calls, refreshScheduled } = useScheduledCalls();

  async function handleCancel(id) {
    if (!window.confirm('Cancel this scheduled call? It will be permanently deleted.')) return;
    try { await api.deleteCall(id); refreshScheduled(); }
    catch (err) { alert(err.message); }
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>Scheduled Calls</h2>
      </div>

      {calls.length === 0 ? (
        <div className="empty-state">
          <p>No scheduled calls at this time.</p>
        </div>
      ) : (
        <div className="scheduled-section" style={{ margin: 0, padding: 0, border: 'none', background: 'none' }}>
          <div className="calls-grid">
            {calls.map(call => (
              <ScheduledCard
                key={call.id}
                call={call}
                canCancel={canInModule('dispatch', 'create_calls')}
                onCancel={() => handleCancel(call.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ScheduledCard({ call, canCancel, onCancel }) {
  const now = new Date();

  const hasDueTime = !!call.due_time;
  const dueTime = hasDueTime ? new Date(call.due_time.replace(' ', 'T') + 'Z') : null;
  const notifyTime = new Date(call.scheduled_for.replace(' ', 'T') + 'Z');
  const countdownTarget = dueTime || notifyTime;

  const diffMs = countdownTarget - now;
  const diffMin = Math.max(0, Math.floor(diffMs / 60000));
  const hours = Math.floor(diffMin / 60);
  const mins = diffMin % 60;

  let countdown;
  if (diffMs <= 0) {
    countdown = hasDueTime ? 'Due now' : 'Activating soon...';
  } else if (hours > 0) {
    countdown = `${hasDueTime ? 'Due in ' : ''}${hours}h ${mins}m`;
  } else {
    countdown = `${hasDueTime ? 'Due in ' : ''}${mins}m`;
  }

  const displayTarget = dueTime || notifyTime;
  const displayDate = displayTarget.toLocaleDateString('en-US', {
    timeZone: 'America/Chicago', month: 'short', day: 'numeric'
  });
  const displayTime = displayTarget.toLocaleTimeString('en-US', {
    timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit'
  });

  const notifyDisplay = hasDueTime ? notifyTime.toLocaleTimeString('en-US', {
    timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit'
  }) : null;

  return (
    <div className="call-card status-scheduled">
      <div className="call-card-header">
        <div className="call-card-title">
          <span className="scheduled-badge">Scheduled</span>
          {call.call_type_name && <span style={{fontSize: 13, color: 'var(--text-muted)', marginLeft: 8}}>{call.call_type_name}</span>}
        </div>
        <span className="scheduled-countdown">{countdown}</span>
      </div>
      <div className="call-card-body">
        <div className="call-detail"><strong>Location:</strong> {call.property_name || call.location}</div>
        {call.pickup_location && <div className="call-detail"><strong>Pickup:</strong> {call.pickup_location}</div>}
        {call.dropoff_location && <div className="call-detail"><strong>Drop-off:</strong> {call.dropoff_location}</div>}
        {call.region && <div className="call-detail"><strong>Region:</strong> {call.region}</div>}
        <div className="call-detail scheduled-time">
          <strong>{hasDueTime ? 'Due:' : 'Scheduled:'}</strong> {displayDate} at {displayTime} CT
        </div>
        {hasDueTime && notifyDisplay && (
          <div className="call-detail" style={{fontSize: 13, color: 'var(--text-muted)'}}>
            Drivers notified at {notifyDisplay} CT
          </div>
        )}
        {call.vehicle_info && <div className="call-detail"><strong>Vehicle:</strong> {call.vehicle_info}</div>}
        {call.notes && <div className="call-detail"><strong>Notes:</strong> {call.notes}</div>}
        {call.created_by_name && <div className="call-detail" style={{fontSize: 13, color: 'var(--text-muted)'}}>Created by {call.created_by_name}</div>}
      </div>
      {canCancel && (
        <div className="call-card-actions">
          <button className="btn btn-danger btn-sm" onClick={onCancel}>Cancel Call</button>
        </div>
      )}
    </div>
  );
}
